import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getStoredToken } from '../../utils/tokenStorage';
import {
  X, Download, ShieldCheck, ShieldAlert, Upload, Link2, Unlink,
  FileText, FlaskConical, Dna, BookOpen, Presentation, Microscope,
  FileSpreadsheet, Image, FileIcon, Copy, Check, Table2, Search,
  PackageCheck,
} from 'lucide-react';
import {
  documentsApi,
  Document,
  DocumentDetail,
  DocumentVersion,
  DocumentLink,
  AuditEntry,
  VerifyResult,
} from '../../api/documents';
import apiClient from '../../api/client';
import { chemistryApi } from '../../api/chemistry';
import { useUIStore } from '../../stores/uiStore';
import { formatDateTime } from '../../utils/dateFormat';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Record<string, string> = {
  chemistry: '#0D9488', biology: '#2563EB', literature: '#6B7280',
  presentation: '#8B5CF6', report: '#4B5563', image: '#EA580C',
  structure: '#2563EB', data: '#F59E0B', other: '#9CA3AF',
};

const ENTITY_COLORS: Record<string, string> = {
  molecule: '#0D9488', reaction: '#8B5CF6', target: '#EF4444',
  patent: '#F59E0B', pathway: '#2563EB', bioactivity: '#10B981', file: '#6B7280',
};

const LINK_TYPES = ['source', 'reference', 'attachment', 'evidence', 'output'];
const ENTITY_TYPES = ['molecule', 'reaction', 'target', 'patent', 'pathway'];

const ACTION_COLORS: Record<string, string> = {
  UPLOAD: '#0D9488', DOWNLOAD: '#2563EB', VIEW: '#9CA3AF',
  VERSION: '#8B5CF6', LINK: '#F59E0B', UNLINK: '#F59E0B',
  ARCHIVE: '#EF4444', RESTORE: '#EF4444', VERIFY: '#10B981',
  VERIFY_FAILED: '#EF4444', RENAME: '#6B7280', TAG: '#6B7280',
  SHARE: '#6B7280', DENIED: '#EF4444',
};

function categoryIcon(category: string, size = 32) {
  const props = { size, strokeWidth: 1.5 };
  switch (category) {
    case 'chemistry': return <FlaskConical {...props} />;
    case 'biology': return <Dna {...props} />;
    case 'literature': return <BookOpen {...props} />;
    case 'presentation': return <Presentation {...props} />;
    case 'report': return <FileText {...props} />;
    case 'image': return <Image {...props} />;
    case 'structure': return <Microscope {...props} />;
    case 'data': return <FileSpreadsheet {...props} />;
    default: return <FileIcon {...props} />;
  }
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function truncHash(hash: string, len = 12): string {
  return hash.length > len ? hash.slice(0, len) + '...' : hash;
}

const FILE_TYPE_LABELS: Record<string, string> = {
  'chemical/x-sdf': 'SDF (Structure-Data)',
  'chemical/x-mdl-sdfile': 'SDF (Structure-Data)',
  'chemical/x-mol': 'MOL (Molecule)',
  'chemical/x-mdl-molfile': 'MOL (Molecule)',
  'chemical/x-smiles': 'SMILES',
  'chemical/x-rxn': 'RXN (Reaction)',
  'chemical/x-rdf': 'RDF (Reaction Data)',
  'text/csv': 'CSV (Comma-Separated)',
  'application/pdf': 'PDF Document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
  'application/vnd.ms-excel': 'Excel Spreadsheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
  'image/png': 'PNG Image',
  'image/jpeg': 'JPEG Image',
  'image/svg+xml': 'SVG Image',
  'text/plain': 'Plain Text',
  'application/json': 'JSON Data',
};

const EXTENSION_LABELS: Record<string, string> = {
  sdf: 'SDF (Structure-Data)',
  mol: 'MOL (Molecule)',
  smi: 'SMILES',
  smiles: 'SMILES',
  rxn: 'RXN (Reaction)',
  rdf: 'RDF (Reaction Data)',
  csv: 'CSV (Comma-Separated)',
  xlsx: 'Excel Spreadsheet',
  xls: 'Excel Spreadsheet',
  pdf: 'PDF Document',
  pptx: 'PowerPoint',
  ppt: 'PowerPoint',
  docx: 'Word Document',
  doc: 'Word Document',
  fasta: 'FASTA (Sequences)',
  fa: 'FASTA (Sequences)',
  png: 'PNG Image',
  jpg: 'JPEG Image',
  jpeg: 'JPEG Image',
  svg: 'SVG Image',
  json: 'JSON Data',
  txt: 'Plain Text',
};

const CATEGORY_FILE_LABELS: Record<string, string> = {
  chemistry: 'Chemistry File',
  biology: 'Biology File',
  structure: 'Structure File',
  data: 'Data File',
  literature: 'Literature',
  presentation: 'Presentation',
  report: 'Report',
  image: 'Image',
};

function friendlyFileType(mimeType: string | null | undefined, filename: string, category: string): string {
  if (mimeType && FILE_TYPE_LABELS[mimeType]) return FILE_TYPE_LABELS[mimeType];
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext && EXTENSION_LABELS[ext]) return EXTENSION_LABELS[ext];
  if (CATEGORY_FILE_LABELS[category]) return CATEGORY_FILE_LABELS[category];
  if (mimeType && mimeType !== 'unknown' && mimeType !== 'application/octet-stream') return mimeType;
  return 'Document';
}

// ---------------------------------------------------------------------------
// Sub-tabs
// ---------------------------------------------------------------------------

type PanelTab = 'details' | 'versions' | 'links' | 'audit';

// ---------------------------------------------------------------------------
// Details Tab
// ---------------------------------------------------------------------------

const DetailsTab: React.FC<{ detail: DocumentDetail; onRefresh: () => void; chemFileId?: string; dataFileId?: string | null }> = ({ detail, onRefresh, chemFileId, dataFileId }) => {
  const showNotification = useUIStore((s) => s.showNotification);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(detail.description || '');
  const [copiedHash, setCopiedHash] = useState(false);
  const [downloadingPack, setDownloadingPack] = useState(false);

  const currentVersion = detail.versions?.[0];
  const hash = currentVersion?.sha256_hash || '';

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const result = await documentsApi.verifyIntegrity(detail.id);
      setVerifyResult(result);
    } catch {
      showNotification('error', 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleDescSave = async () => {
    try {
      await documentsApi.updateDocument(detail.id, { description: descDraft });
      setEditingDesc(false);
      onRefresh();
    } catch {
      showNotification('error', 'Failed to update description');
    }
  };

  const handleDownload = async () => {
    // Chemistry files use the files download endpoint
    const effectiveChemFileId = chemFileId || dataFileId;
    if (effectiveChemFileId) {
      try {
        const blob = await chemistryApi.downloadFile(effectiveChemFileId);
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = detail.original_filename;
        a.click();
        URL.revokeObjectURL(blobUrl);
      } catch {
        showNotification('error', 'Download failed');
      }
      return;
    }

    // Real documents use the documents endpoint
    const token = getStoredToken();
    const url = documentsApi.downloadUrl(detail.id);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = detail.original_filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => showNotification('error', 'Download failed'));
  };

  const handleDownloadEvidencePack = () => {
    setDownloadingPack(true);
    const token = getStoredToken();
    fetch(`/api/v1/documents/${detail.id}/evidence-pack`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `audit-report-${detail.display_name.replace(/\s+/g, '_')}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
        showNotification('success', 'Audit report downloaded');
      })
      .catch(() => showNotification('error', 'Audit report download failed'))
      .finally(() => setDownloadingPack(false));
  };

  const copyHash = () => {
    navigator.clipboard.writeText(hash);
    setCopiedHash(true);
    setTimeout(() => setCopiedHash(false), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Description */}
      <div>
        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 block">Description</label>
        {editingDesc ? (
          <div className="flex gap-2">
            <textarea
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              style={{ borderColor: 'var(--card-border)' }}
              rows={2}
              autoFocus
            />
            <div className="flex flex-col gap-1">
              <button onClick={handleDescSave} className="px-2 py-1 text-xs bg-emerald-500 text-white rounded">Save</button>
              <button onClick={() => setEditingDesc(false)} className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded">Cancel</button>
            </div>
          </div>
        ) : (
          <p
            onClick={() => { setDescDraft(detail.description || ''); setEditingDesc(true); }}
            className="text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-2 py-1 -mx-2"
            style={{ color: detail.description ? 'var(--text-primary)' : 'var(--text-muted)' }}
          >
            {detail.description || 'Click to add description...'}
          </p>
        )}
      </div>

      {/* Property Grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <span className="text-xs text-gray-400 block">File Type</span>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-secondary)' }}>
            {friendlyFileType(detail.mime_type, detail.original_filename, detail.category)}
          </span>
        </div>
        <div>
          <span className="text-xs text-gray-400 block">Category</span>
          <span
            style={{
              display: 'inline-block', padding: '1px 8px', borderRadius: 9999, fontSize: 11,
              fontWeight: 600, color: '#fff', backgroundColor: CATEGORY_COLORS[detail.category] || '#9CA3AF',
              textTransform: 'capitalize',
            }}
          >
            {detail.category}
          </span>
        </div>
        <div>
          <span className="text-xs text-gray-400 block">Sharing</span>
          <span className="text-gray-600 dark:text-gray-400" style={{ textTransform: 'capitalize' }}>{detail.sharing_policy}</span>
        </div>
        <div>
          <span className="text-xs text-gray-400 block">Version</span>
          <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>v{detail.current_version}</span>
        </div>
        <div>
          <span className="text-xs text-gray-400 block">Created</span>
          <span className="text-gray-600 dark:text-gray-400">{formatDateTime(detail.created_at)}</span>
        </div>
        <div>
          <span className="text-xs text-gray-400 block">Updated</span>
          <span className="text-gray-600 dark:text-gray-400">{formatDateTime(detail.updated_at)}</span>
        </div>
      </div>

      {/* SHA-256 */}
      {hash && (
        <div>
          <span className="text-xs text-gray-400 block mb-1">SHA-256</span>
          <button
            onClick={copyHash}
            className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-secondary)' }}>
              {truncHash(hash, 24)}
            </span>
            {copiedHash ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} className="text-gray-400" />}
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={handleDownload}
          className="flex-1 flex items-center justify-center gap-2 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Download size={14} /> Download
        </button>
        <div className="relative group/verify">
          <button
            onClick={handleVerify}
            disabled={verifying}
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
            style={{ borderColor: 'var(--card-border)', color: 'var(--text-secondary)' }}
          >
            {verifying ? (
              <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <ShieldCheck size={14} />
            )}
            Check Integrity
          </button>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 px-3 py-2 text-xs text-gray-100 bg-gray-800 rounded-lg shadow-lg whitespace-nowrap opacity-0 pointer-events-none transition-opacity duration-200 group-hover/verify:opacity-100 group-hover/verify:delay-300">
            Recalculates SHA-256 and compares with stored hash
          </div>
        </div>
      </div>

      {/* Export Audit Report */}
      <div className="relative group/audit-report">
        <button
          onClick={handleDownloadEvidencePack}
          disabled={downloadingPack}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg border transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
          style={{ borderColor: 'var(--card-border)', color: 'var(--text-secondary)' }}
        >
          {downloadingPack ? (
            <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <PackageCheck size={14} />
          )}
          Export Audit Report
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 px-3 py-2 text-xs text-gray-100 bg-gray-800 rounded-lg shadow-lg whitespace-nowrap opacity-0 pointer-events-none transition-opacity duration-200 group-hover/audit-report:opacity-100 group-hover/audit-report:delay-300">
          Downloads a ZIP with the file, version history, and audit trail
        </div>
      </div>

      {/* Check Integrity result */}
      {verifyResult && (
        <div
          className="p-3 rounded-lg text-sm"
          style={{
            backgroundColor: verifyResult.match ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          }}
        >
          <div className="flex items-center gap-2 font-medium" style={{ color: verifyResult.match ? '#059669' : '#DC2626' }}>
            {verifyResult.match ? <ShieldCheck size={16} /> : <ShieldAlert size={16} />}
            {verifyResult.match ? 'Integrity verified — hashes match' : 'INTEGRITY MISMATCH — file may be corrupted'}
          </div>
          <p className="text-xs mt-1.5" style={{ color: verifyResult.match ? '#047857' : '#B91C1C' }}>
            {verifyResult.match
              ? 'The stored SHA-256 hash matches the current file contents. This file has not been modified since upload.'
              : 'The stored hash does not match the current file. This may indicate the file was modified, corrupted during transfer, or tampered with. Re-upload the original to restore integrity.'}
          </p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Versions Tab
// ---------------------------------------------------------------------------

const VersionsTab: React.FC<{ detail: DocumentDetail; onRefresh: () => void }> = ({ detail, onRefresh }) => {
  const showNotification = useUIStore((s) => s.showNotification);

  const handleUploadVersion = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const note = window.prompt('Change note (optional):');
      try {
        await documentsApi.uploadVersion(detail.id, file, note || undefined);
        showNotification('success', 'New version uploaded');
        onRefresh();
      } catch {
        showNotification('error', 'Upload failed');
      }
    };
    input.click();
  };

  const handleDownloadVersion = (version: DocumentVersion) => {
    const token = getStoredToken();
    const url = documentsApi.downloadVersionUrl(detail.id, version.version_number);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = detail.original_filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => showNotification('error', 'Download failed'));
  };

  return (
    <div>
      <button
        onClick={handleUploadVersion}
        className="w-full flex items-center justify-center gap-2 py-2 mb-4 text-sm font-semibold text-emerald-600 border-2 border-dashed rounded-lg hover:bg-emerald-50 transition-colors"
        style={{ borderColor: '#0D9488' }}
      >
        <Upload size={14} /> Upload New Version
      </button>

      <div className="space-y-3">
        {(detail.versions || []).map((ver) => {
          const isCurrent = ver.version_number === detail.current_version;
          return (
            <div
              key={ver.id}
              className="p-3 rounded-lg border"
              style={{
                borderColor: isCurrent ? '#0D9488' : 'var(--card-border)',
                borderLeftWidth: isCurrent ? 3 : 1,
                backgroundColor: isCurrent ? 'rgba(13,148,136,0.04)' : 'transparent',
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13 }}>
                  v{ver.version_number}
                  {isCurrent && <span className="ml-2 text-xs font-normal text-emerald-600">current</span>}
                </span>
                <button
                  onClick={() => handleDownloadVersion(ver)}
                  className="text-gray-400 hover:text-emerald-600 transition-colors"
                >
                  <Download size={14} />
                </button>
              </div>
              <div className="text-xs text-gray-400 space-y-0.5">
                <div>{formatDateTime(ver.created_at)}</div>
                <div>{formatBytes(ver.file_size_bytes)}</div>
                {ver.change_note && (
                  <div className="text-gray-600 dark:text-gray-400 italic mt-1">"{ver.change_note}"</div>
                )}
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--text-muted)' }}>
                  {truncHash(ver.sha256_hash, 16)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Entity Search helpers
// ---------------------------------------------------------------------------

interface EntitySearchResult {
  id: number;
  label: string;
  detail: string;
}

const SEARCHABLE_TYPES = new Set(['molecule', 'target']);

async function searchEntities(
  entityType: string,
  query: string,
  projectId: number,
): Promise<EntitySearchResult[]> {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  if (entityType === 'molecule') {
    // Get project files, then query molecules with name filter
    try {
      const filesResp = await apiClient.get(`/files`, { params: { project_id: projectId } });
      const files: Array<{ id: number; molecule_count?: number }> = filesResp.data;
      const withMolecules = files.filter(f => (f.molecule_count || 0) > 0);
      const results: EntitySearchResult[] = [];

      // Query up to 3 files in parallel for speed
      const queries = withMolecules.slice(0, 3).map(async (file) => {
        try {
          const resp = await apiClient.post(`/files/${file.id}/molecules/query`, {
            startRow: 0,
            endRow: 10,
            filterModel: {
              Name: { filterType: 'text', type: 'contains', filter: query.trim() },
            },
          });
          const rows = resp.data?.rows || [];
          for (const row of rows) {
            if (results.length >= 10) break;
            const name = row.Name || row.name || '';
            const smiles = row.SMILES || row.smiles || '';
            const mw = row.MW || row['Molecular Weight'] || '';
            results.push({
              id: row.id,
              label: name || `Molecule #${row.id}`,
              detail: [smiles ? smiles.slice(0, 40) + (smiles.length > 40 ? '...' : '') : '', mw ? `MW: ${mw}` : ''].filter(Boolean).join(' | '),
            });
          }
        } catch { /* skip file */ }
      });
      await Promise.all(queries);

      // If no name matches, also try SMILES filter
      if (results.length === 0 && withMolecules.length > 0) {
        try {
          const resp = await apiClient.post(`/files/${withMolecules[0].id}/molecules/query`, {
            startRow: 0,
            endRow: 10,
            filterModel: {
              SMILES: { filterType: 'text', type: 'contains', filter: query.trim() },
            },
          });
          for (const row of (resp.data?.rows || [])) {
            if (results.length >= 10) break;
            results.push({
              id: row.id,
              label: row.Name || row.name || `Molecule #${row.id}`,
              detail: (row.SMILES || row.smiles || '').slice(0, 50),
            });
          }
        } catch { /* ignore */ }
      }

      return results;
    } catch {
      return [];
    }
  }

  if (entityType === 'target') {
    try {
      const resp = await apiClient.get('/biologics');
      const biologics: Array<{
        id: number;
        name: string;
        uniprot_id?: string;
        organism?: string;
        properties?: Record<string, any>;
      }> = resp.data?.biologics || resp.data || [];
      return biologics
        .filter(b => {
          const n = (b.name || '').toLowerCase();
          const u = (b.uniprot_id || '').toLowerCase();
          const desc = (b.properties?.description || '').toLowerCase();
          return n.includes(q) || u.includes(q) || desc.includes(q);
        })
        .slice(0, 10)
        .map(b => ({
          id: b.id,
          label: b.name || `Target #${b.id}`,
          detail: [b.uniprot_id, b.properties?.description?.slice(0, 60)].filter(Boolean).join(' | '),
        }));
    } catch {
      return [];
    }
  }

  return [];
}

// ---------------------------------------------------------------------------
// Links Tab
// ---------------------------------------------------------------------------

const LinksTab: React.FC<{ detail: DocumentDetail; onRefresh: () => void }> = ({ detail, onRefresh }) => {
  const showNotification = useUIStore((s) => s.showNotification);
  const [links, setLinks] = useState<DocumentLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [entityType, setEntityType] = useState('molecule');
  const [linkType, setLinkType] = useState('source');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<EntitySearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<EntitySearchResult | null>(null);
  const [fallbackId, setFallbackId] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const hasSearch = SEARCHABLE_TYPES.has(entityType);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await documentsApi.listLinks(detail.id);
      setLinks(data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [detail.id]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  // Reset search when entity type changes
  useEffect(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedEntity(null);
    setFallbackId('');
  }, [entityType]);

  // Debounced search
  useEffect(() => {
    if (!hasSearch || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchEntities(entityType, searchQuery, detail.project_id);
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, entityType, hasSearch, detail.project_id]);

  const handleSelectEntity = (result: EntitySearchResult) => {
    setSelectedEntity(result);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleCreate = async () => {
    const entityId = selectedEntity ? selectedEntity.id : parseInt(fallbackId);
    if (isNaN(entityId)) {
      showNotification('error', 'Please select an entity or enter a valid ID');
      return;
    }
    try {
      await documentsApi.createLink(detail.id, entityType, entityId, linkType);
      showNotification('success', 'Link created');
      setShowForm(false);
      setSelectedEntity(null);
      setFallbackId('');
      setSearchQuery('');
      fetchLinks();
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to create link');
    }
  };

  const handleRemove = async (linkId: number) => {
    try {
      await documentsApi.removeLink(detail.id, linkId);
      fetchLinks();
    } catch {
      showNotification('error', 'Failed to remove link');
    }
  };

  const SEARCH_PLACEHOLDERS: Record<string, string> = {
    molecule: 'Search by name or SMILES...',
    target: 'Search by name or UniProt ID...',
    reaction: 'Enter reaction ID',
    patent: 'Enter patent ID',
    pathway: 'Enter pathway ID',
  };

  return (
    <div>
      <button
        onClick={() => { setShowForm(!showForm); setSelectedEntity(null); setSearchQuery(''); setFallbackId(''); }}
        className="w-full flex items-center justify-center gap-2 py-2 mb-4 text-sm font-semibold text-emerald-600 border-2 border-dashed rounded-lg hover:bg-emerald-50 transition-colors"
        style={{ borderColor: '#0D9488' }}
      >
        <Link2 size={14} /> Link To...
      </button>

      {showForm && (
        <div className="p-3 mb-4 rounded-lg border space-y-3" style={{ borderColor: 'var(--card-border)' }}>
          {/* Entity type selector */}
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 block">Entity Type</label>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              className="w-full px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-900"
              style={{ borderColor: 'var(--card-border)' }}
            >
              {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Search input or fallback */}
          <div className="relative">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 block">
              {hasSearch ? 'Search' : 'Entity ID'}
            </label>
            {hasSearch ? (
              <>
                {/* Selected entity chip */}
                {selectedEntity ? (
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                    style={{ borderColor: '#0D9488', backgroundColor: 'rgba(13,148,136,0.06)' }}
                  >
                    <span
                      style={{
                        display: 'inline-block', padding: '1px 8px', borderRadius: 9999, fontSize: 10,
                        fontWeight: 600, color: '#fff',
                        backgroundColor: ENTITY_COLORS[entityType] || '#9CA3AF',
                        textTransform: 'capitalize',
                      }}
                    >
                      {entityType}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                        {selectedEntity.label}
                      </div>
                      {selectedEntity.detail && (
                        <div className="text-xs truncate" style={{ color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }}>
                          {selectedEntity.detail}
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      #{selectedEntity.id}
                    </span>
                    <button
                      onClick={() => { setSelectedEntity(null); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={SEARCH_PLACEHOLDERS[entityType] || 'Search...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm rounded border bg-white dark:bg-gray-900"
                        style={{ borderColor: 'var(--card-border)' }}
                        autoFocus
                      />
                      {searching && (
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                          <div className="w-3.5 h-3.5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {/* Results dropdown */}
                    {searchResults.length > 0 && (
                      <div
                        className="mt-1 rounded-lg border shadow-lg overflow-hidden max-h-60 overflow-y-auto"
                        style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}
                      >
                        {searchResults.map((result) => (
                          <button
                            key={`${entityType}-${result.id}`}
                            onClick={() => handleSelectEntity(result)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b last:border-b-0"
                            style={{ borderColor: 'var(--card-border)' }}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                {result.label}
                              </span>
                              <span className="text-xs text-gray-400 ml-2 flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                #{result.id}
                              </span>
                            </div>
                            {result.detail && (
                              <div className="text-xs truncate mt-0.5" style={{ color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }}>
                                {result.detail}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {searchQuery.trim() && !searching && searchResults.length === 0 && (
                      <p className="text-xs text-gray-400 mt-1 px-1">No results found. You can enter an ID directly below.</p>
                    )}
                    {/* Manual ID fallback when search returns nothing */}
                    {searchQuery.trim() && !searching && searchResults.length === 0 && (
                      <div className="mt-2">
                        <input
                          type="number"
                          placeholder="Or enter entity ID directly"
                          value={fallbackId}
                          onChange={(e) => setFallbackId(e.target.value)}
                          className="w-full px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-900"
                          style={{ borderColor: 'var(--card-border)' }}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              /* Non-searchable entity types: raw ID input */
              <div>
                <input
                  type="number"
                  placeholder={SEARCH_PLACEHOLDERS[entityType] || 'Enter entity ID'}
                  value={fallbackId}
                  onChange={(e) => setFallbackId(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-900"
                  style={{ borderColor: 'var(--card-border)' }}
                />
                <p className="text-xs text-gray-400 mt-1">Enter the entity ID directly</p>
              </div>
            )}
          </div>

          {/* Link type */}
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 block">Link Type</label>
            <select
              value={linkType}
              onChange={(e) => setLinkType(e.target.value)}
              className="w-full px-2 py-1.5 text-sm rounded border bg-white dark:bg-gray-900"
              style={{ borderColor: 'var(--card-border)' }}
            >
              {LINK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Create / Cancel */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleCreate}
              disabled={!selectedEntity && !fallbackId}
              className="flex-1 py-1.5 text-sm font-semibold bg-emerald-500 text-white rounded hover:bg-emerald-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Create Link
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : links.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">No linked entities</p>
      ) : (
        <div className="space-y-2">
          {links.map((link) => (
            <div key={link.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
              <span
                style={{
                  display: 'inline-block', padding: '1px 8px', borderRadius: 9999, fontSize: 11,
                  fontWeight: 600, color: '#fff',
                  backgroundColor: ENTITY_COLORS[link.entity_type] || '#9CA3AF',
                  textTransform: 'capitalize',
                }}
              >
                {link.entity_type}
              </span>
              <span className="text-sm font-medium" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                #{link.entity_id}
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500">
                {link.link_type}
              </span>
              <button
                onClick={() => handleRemove(link.id)}
                className="ml-auto text-gray-300 hover:text-red-500 transition-colors"
              >
                <Unlink size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Audit Tab
// ---------------------------------------------------------------------------

const AuditTab: React.FC<{ documentId: number }> = ({ documentId }) => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    documentsApi.getAuditTrail(documentId)
      .then(setEntries)
      .catch((err) => {
        const msg = err?.response?.data?.detail;
        if (typeof msg === 'object' && msg?.message) setError(msg.message);
        else if (typeof msg === 'string') setError(msg);
        else setError('Unable to load audit trail');
      })
      .finally(() => setLoading(false));
  }, [documentId]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error) return <p className="text-sm text-gray-400 text-center py-8">{error}</p>;
  if (entries.length === 0) return <p className="text-sm text-gray-400 text-center py-8">No audit entries</p>;

  return (
    <div className="space-y-2">
      {entries.map((entry) => (
        <div key={entry.id} className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
          <span
            style={{
              display: 'inline-block', padding: '1px 8px', borderRadius: 9999, fontSize: 10,
              fontWeight: 700, color: '#fff', minWidth: 60, textAlign: 'center',
              backgroundColor: ACTION_COLORS[entry.action] || '#9CA3AF',
            }}
          >
            {entry.action}
          </span>
          <div className="flex-1 min-w-0">
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-muted)' }}>
              {formatDateTime(entry.timestamp)}
            </div>
            {entry.detail && Object.keys(entry.detail).length > 0 && (
              <div className="text-xs text-gray-500 mt-0.5 truncate">
                {Object.entries(entry.detail).map(([k, v]) => `${k}: ${v}`).join(', ')}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

interface DocumentDetailPanelProps {
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  initialTab?: PanelTab;
  /** If provided, shows "Open in Grid" for chemistry files with a matching data file ID */
  dataFileId?: string | null;
  onNavigateToEditor?: (fileId: string) => void;
  /** If provided, clicking a folder pill navigates to that folder in the sidebar */
  onFolderNavigate?: (folderId: string) => void;
}

const DocumentDetailPanel: React.FC<DocumentDetailPanelProps> = ({
  document: doc,
  isOpen,
  onClose,
  onRefresh,
  initialTab = 'details',
  dataFileId,
  onNavigateToEditor,
  onFolderNavigate,
}) => {
  const [activeTab, setActiveTab] = useState<PanelTab>(initialTab);
  const [detail, setDetail] = useState<DocumentDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);

  // Chemistry files (from files table) have negative IDs and a _chemFileId property
  const chemFileId = (doc as any)?._chemFileId as string | undefined;
  const isChemFile = doc != null && doc.id < 0 && !!chemFileId;

  const fetchDetail = useCallback(async () => {
    if (!doc) return;
    if (isChemFile) {
      // Fetch from chemistry files API and convert to DocumentDetail shape
      const file = await chemistryApi.getFile(chemFileId!);
      setDetail({
        ...doc,
        versions: [{
          id: 0,
          document_id: doc.id,
          version_number: 1,
          uploaded_by: file.uploadedBy || '',
          storage_path: '',
          file_size_bytes: file.fileSize || 0,
          sha256_hash: '',
          scan_status: 'COMPLETE',
          change_note: null,
          created_at: file.createdDate || doc.created_at,
        }],
      });
    } else {
      const d = await documentsApi.getDocument(doc.id);
      setDetail(d);
    }
  }, [doc, isChemFile, chemFileId]);

  useEffect(() => {
    if (!doc || !isOpen) { setDetail(null); return; }
    setLoading(true);
    fetchDetail().catch(() => {}).finally(() => setLoading(false));
  }, [doc, isOpen, fetchDetail]);

  const handleRefresh = useCallback(() => {
    if (!doc) return;
    fetchDetail().catch(() => {});
    onRefresh();
  }, [doc, fetchDetail, onRefresh]);

  const TABS: { id: PanelTab; label: string }[] = [
    { id: 'details', label: 'Details' },
    { id: 'versions', label: 'Versions' },
    // { id: 'links', label: 'Links' },  // Hidden — entity linking not yet ready for production
    { id: 'audit', label: 'History' },
  ];

  return (
    <div
      className="flex-shrink-0 border-l flex flex-col overflow-hidden"
      style={{
        backgroundColor: 'var(--card-bg)',
        borderColor: 'var(--card-border)',
        width: isOpen ? 420 : 0,
        minWidth: isOpen ? 420 : 0,
        transition: 'width 300ms ease, min-width 300ms ease',
      }}
    >
      {isOpen && doc && (
        <>
          {/* Header */}
          <div className="px-4 py-3 border-b flex items-start gap-3" style={{ borderColor: 'var(--card-border)' }}>
            <span style={{ color: CATEGORY_COLORS[doc.category] || '#9CA3AF', marginTop: 2 }}>
              {categoryIcon(doc.category, 28)}
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                {doc.display_name}
              </h3>
              <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-muted)' }} className="truncate">
                {doc.original_filename}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span
                  style={{
                    display: 'inline-block', padding: '1px 8px', borderRadius: 9999, fontSize: 10,
                    fontWeight: 600, color: '#fff',
                    backgroundColor: CATEGORY_COLORS[doc.category] || '#9CA3AF',
                    textTransform: 'capitalize',
                  }}
                >
                  {doc.category}
                </span>
                <span
                  style={{
                    fontSize: 10, fontWeight: 600,
                    color: doc.processing_status === 'COMPLETE' ? '#10B981' : '#F59E0B',
                  }}
                >
                  {doc.processing_status}
                </span>
              </div>
              {/* Folder pills */}
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {[
                  { id: 'all', label: 'All Files' },
                  { id: doc.category?.toLowerCase(), label: doc.category ? doc.category.charAt(0).toUpperCase() + doc.category.slice(1) : '' },
                  ...(doc.tags || []).filter(t => t.startsWith('folder:')).map(t => ({
                    id: `custom:${t.slice(7)}`,
                    label: t.slice(7),
                  })),
                ].filter(f => f.label).map(folder => (
                  <button
                    key={folder.id}
                    onClick={() => onFolderNavigate?.(folder.id)}
                    className="text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                    style={{
                      color: 'var(--text-muted)',
                      backgroundColor: 'var(--hover-bg)',
                      borderRadius: 4,
                      cursor: onFolderNavigate ? 'pointer' : 'default',
                    }}
                  >
                    {folder.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1">
              {dataFileId && onNavigateToEditor && (
                <button
                  onClick={() => onNavigateToEditor(dataFileId)}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                  title="Open molecule data in grid editor"
                >
                  <Table2 size={13} />
                  <span>Open in Grid</span>
                </button>
              )}
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Tab Bar */}
          <div className="px-3 flex gap-0.5 border-b" style={{ borderColor: 'var(--card-border)' }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-3 py-2 text-xs whitespace-nowrap transition-colors"
                style={{
                  color: activeTab === tab.id ? '#0D9488' : 'var(--text-secondary)',
                  borderBottom: activeTab === tab.id ? '2px solid #0D9488' : '2px solid transparent',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading || !detail ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {activeTab === 'details' && <DetailsTab detail={detail} onRefresh={handleRefresh} chemFileId={chemFileId} dataFileId={dataFileId} />}
                {activeTab === 'versions' && <VersionsTab detail={detail} onRefresh={handleRefresh} />}
                {activeTab === 'links' && <LinksTab detail={detail} onRefresh={handleRefresh} />}
                {activeTab === 'audit' && <AuditTab documentId={detail.id} />}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentDetailPanel;
